import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PuppeteerLivePreviewer from './PuppeteerLivePreviewer';
import DownloadProgressModal from './DownloadProgressModal';
import {
  getPreviewHtml,
  downloadPdf,
  downloadPdfWithProgress,
  parseUploadedResume,
  getResumeById,
} from './puppeteerApi';
import TopMenu from '../TopMenu';
import './PuppeteerResumeEditor.css';

const defaultData = {
  firstName: '',
  surname: '',
  email: '',
  phone: '',
  city: '',
  country: '',
  pinCode: '',
  summary: '',
  profileImageBase64: null,
  experiences: [{ jobTitle: '', company: '', startDate: '', endDate: '', description: [''] }],
  educations: [{ degree: '', institution: '', year: '' }],
  skills: [''],
};

const SECTIONS = [
  { key: 'header', label: 'Header', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>
  )},
  { key: 'experience', label: 'Experience', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
  )},
  { key: 'education', label: 'Education', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5"/></svg>
  )},
  { key: 'skills', label: 'Skills', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
  )},
  { key: 'summary', label: 'Summary', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 7h10M7 12h10M7 17h6"/></svg>
  )},
  { key: 'other', label: 'Other sections', icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
  )},
];

const PARSE_STEPS = [
  { label: 'Uploading your resume', sub: 'Securely transferring file' },
  { label: 'Analyzing document', sub: 'Reading structure & layout' },
  { label: 'Extracting information', sub: 'Pulling out key details' },
  { label: 'Mapping your data', sub: 'Organizing into sections' },
  { label: 'Finalizing', sub: 'Preparing your editor' },
];

const ResumeParsingOverlay = ({ active, done }) => {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!active) { setStep(0); setProgress(0); return; }
    // Step progression
    const stepInterval = setInterval(() => {
      setStep(s => (s < PARSE_STEPS.length - 1 ? s + 1 : s));
    }, 2200);
    // Smooth progress bar
    const progInterval = setInterval(() => {
      setProgress(p => {
        if (p >= 90) return p;
        return p + (90 - p) * 0.04;
      });
    }, 80);
    return () => { clearInterval(stepInterval); clearInterval(progInterval); };
  }, [active]);

  useEffect(() => {
    if (done) setProgress(100);
  }, [done]);

  if (!active && !done) return null;

  return (
    <div className="rpo-overlay">
      {/* Floating particles */}
      <div className="rpo-particles">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`rpo-particle rpo-p${i + 1}`} />
        ))}
      </div>

      <div className={`rpo-card ${done ? 'rpo-done' : ''}`}>
        {!done ? (
          <>
            {/* Orbiting rings */}
            <div className="rpo-orbit-wrap">
              <div className="rpo-orbit rpo-orbit-1" />
              <div className="rpo-orbit rpo-orbit-2" />
              <div className="rpo-core">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
              </div>
            </div>

            {/* Progress bar */}
            <div className="rpo-prog-wrap">
              <div className="rpo-prog-track">
                <div className="rpo-prog-fill" style={{ width: `${progress}%` }}>
                  <div className="rpo-prog-glow" />
                </div>
              </div>
              <span className="rpo-prog-pct">{Math.round(progress)}%</span>
            </div>

            {/* Steps */}
            <div className="rpo-steps">
              {PARSE_STEPS.map((s, i) => (
                <div key={i} className={`rpo-step ${i < step ? 'rpo-step-done' : ''} ${i === step ? 'rpo-step-active' : ''} ${i > step ? 'rpo-step-pending' : ''}`}>
                  <div className="rpo-step-indicator">
                    {i < step ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : i === step ? (
                      <div className="rpo-step-pulse" />
                    ) : (
                      <div className="rpo-step-dot" />
                    )}
                  </div>
                  <div className="rpo-step-text">
                    <span className="rpo-step-label">{s.label}</span>
                    {i === step && <span className="rpo-step-sub">{s.sub}</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="rpo-success">
            <div className="rpo-success-ring">
              <svg className="rpo-success-svg" width="72" height="72" viewBox="0 0 72 72">
                <circle className="rpo-success-circle" cx="36" cy="36" r="30" fill="none" stroke="#10b981" strokeWidth="3" />
                <polyline className="rpo-success-check" points="24,36 33,45 48,27" fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <p className="rpo-success-title">Resume parsed!</p>
            <p className="rpo-success-sub">Your data is ready to edit</p>
          </div>
        )}
      </div>
    </div>
  );
};

const PuppeteerResumeEditor = ({ theme: themeProp, toggleTheme: toggleThemeProp }) => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = themeProp || location.state?.theme || 'light';
  const toggleTheme = toggleThemeProp || (() => {});

  // Check if we're loading a saved resume (resumeId passed via location state)
  const resumeIdToLoad = location.state?.resumeId || null;

  const [data, setData] = useState(defaultData);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeSection, setActiveSection] = useState('header');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [parseDone, setParseDone] = useState(false);
  const debounceTimer = useRef(null);
  const uploadRef = useRef(null);
  const isDark = theme === 'dark';

  // Download progress modal state
  const [dlModal, setDlModal] = useState({ open: false, percent: 0, message: '', error: null });

  // Load saved resume if resumeId is provided
  useEffect(() => {
    if (!resumeIdToLoad) return;
    (async () => {
      try {
        const result = await getResumeById(resumeIdToLoad);
        if (result?.data) {
          setData({ ...defaultData, ...result.data });
          triggerPreview({ ...defaultData, ...result.data });
        }
      } catch (err) {
        console.error('Failed to load resume:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeIdToLoad]);

  const triggerPreview = useCallback((updatedData) => {
  clearTimeout(debounceTimer.current);
  debounceTimer.current = setTimeout(async () => {
    setPreviewLoading(true);
    try {
      const dto = {
        ...updatedData,
        templateId,
        experiences: updatedData.experiences.map(exp => ({
          ...exp,
          description: Array.isArray(exp.description)
            ? exp.description.filter(Boolean).join('\n')
            : exp.description || ''
        }))
      };
      const html = await getPreviewHtml(dto);
      setPreviewHtml(html);
    } catch (err) {
      console.error('Preview error:', err);
    } finally {
      setPreviewLoading(false);
    }
  }, 800);
}, [templateId]);

  const handleChange = (field, value) => {
    const updated = { ...data, [field]: value };
    setData(updated);
    triggerPreview(updated);
  };

  const handleExpChange = (index, field, value) => {
    const exps = [...data.experiences];
    exps[index] = { ...exps[index], [field]: value };
    const updated = { ...data, experiences: exps };
    setData(updated);
    triggerPreview(updated);
  };

  const removeExperience = (index) => {
    const exps = data.experiences.filter((_, i) => i !== index);
    const updated = { ...data, experiences: exps.length ? exps : [{ jobTitle: '', company: '', startDate: '', endDate: '', description: [''] }] };
    setData(updated);
    triggerPreview(updated);
  };

  const handleDescPointChange = (expIdx, ptIdx, value) => {
    const exps = [...data.experiences];
    const desc = [...exps[expIdx].description];
    desc[ptIdx] = value;
    exps[expIdx] = { ...exps[expIdx], description: desc };
    const updated = { ...data, experiences: exps };
    setData(updated);
    triggerPreview(updated);
  };

  const addDescPoint = (expIdx) => {
    const exps = [...data.experiences];
    exps[expIdx] = { ...exps[expIdx], description: [...exps[expIdx].description, ''] };
    setData({ ...data, experiences: exps });
  };

  const removeDescPoint = (expIdx, ptIdx) => {
    const exps = [...data.experiences];
    const desc = exps[expIdx].description.filter((_, i) => i !== ptIdx);
    exps[expIdx] = { ...exps[expIdx], description: desc.length ? desc : [''] };
    const updated = { ...data, experiences: exps };
    setData(updated);
    triggerPreview(updated);
  };

  const handleEduChange = (index, field, value) => {
    const edus = [...data.educations];
    edus[index] = { ...edus[index], [field]: value };
    const updated = { ...data, educations: edus };
    setData(updated);
    triggerPreview(updated);
  };

  const removeEducation = (index) => {
    const edus = data.educations.filter((_, i) => i !== index);
    const updated = { ...data, educations: edus.length ? edus : [{ degree: '', institution: '', year: '' }] };
    setData(updated);
    triggerPreview(updated);
  };

  const handleSkillChange = (index, value) => {
    const skills = [...data.skills];
    skills[index] = value;
    const updated = { ...data, skills };
    setData(updated);
    triggerPreview(updated);
  };

  const removeSkill = (index) => {
    const skills = data.skills.filter((_, i) => i !== index);
    const updated = { ...data, skills: skills.length ? skills : [''] };
    setData(updated);
    triggerPreview(updated);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      const updated = { ...data, profileImageBase64: base64 };
      setData(updated);
      triggerPreview(updated);
    };
    reader.readAsDataURL(file);
  };

  // Upload resume file → parse and auto-fill form
  const handleUploadResume = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PDF, DOC, or DOCX file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5MB.');
      return;
    }

    setUploadLoading(true);
    setParseDone(false);
    try {
      const response = await parseUploadedResume(file);
      if (!response.wasSuccessful) {
        setUploadLoading(false);
        alert(response.errorMessage || 'Failed to parse resume.');
        return;
      }
      const parsedData = response.parsedData;

      // Map parsedData fields into the DTO shape
      const p = parsedData.personal || {};
      const nameParts = (p.fullName || '').split(' ');
      const firstName = nameParts[0] || '';
      const surname = nameParts.slice(1).join(' ') || '';
      const locationParts = (p.location || '').split(',');
      const city = (locationParts[0] || '').trim();
      const country = (locationParts.slice(1).join(',') || '').trim();

      const experiences = (parsedData.experience || []).map((e) => {
  const parts = (e.duration || '').split(/\s*[-–]\s*/);
  const rawDesc = e.description || '';
  return {
    jobTitle: e.title || '',
    company: e.company || '',
    startDate: (parts[0] || '').trim(),
    endDate: (parts[1] || '').trim(),
    // ✅ Store as array for the bullet point editor
    description: Array.isArray(rawDesc)
      ? rawDesc
      : rawDesc.split('\n').filter(Boolean).length
        ? rawDesc.split('\n').filter(Boolean)
        : [''],
  };
});

      const educations = (parsedData.education || []).map((e) => ({
        degree: e.degree || '',
        institution: e.institution || '',
        year: e.duration || '',
      }));

      const skills = parsedData.skills?.technical?.length ? parsedData.skills.technical : defaultData.skills;

      const mapped = {
        ...defaultData,
        firstName,
        surname,
        email: p.email || '',
        phone: p.phone || '',
        city,
        country,
        linkedIn: p.linkedIn || '',
        gitHub: p.gitHub || '',
        summary: parsedData.objective || '',
        experiences: experiences.length ? experiences : defaultData.experiences,
        educations: educations.length ? educations : defaultData.educations,
        skills,
        certifications: parsedData.certifications || [],
        projects: parsedData.projects || [],
        positions: parsedData.positions || [],
        achievements: parsedData.achievements || [],
      };

      setData(mapped);
      setParseDone(true);
      setTimeout(() => { setUploadLoading(false); setParseDone(false); }, 1500);
      // Pass the raw parse-upload output directly as the preview input
      setPreviewLoading(true);
      try {
        const html = await getPreviewHtml({ ...mapped, templateId });
        setPreviewHtml(html);
      } catch (err) {
        console.error('Preview error:', err);
      } finally {
        setPreviewLoading(false);
      }
      setActiveSection('header');
    } catch (err) {
      setUploadLoading(false);
      setParseDone(false);
      alert('Failed to parse resume. Please try a different file.');
    } finally {
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  // Download with SSE progress
  const handleDownload = async () => {
  const dto = {
    ...data,
    templateId,
    experiences: data.experiences.map(exp => ({
      ...exp,
      description: Array.isArray(exp.description)
        ? exp.description.filter(Boolean).join('\n')
        : exp.description || ''
    }))
  };
  setDlModal({ open: true, percent: 0, message: 'Starting...', error: null });

  await downloadPdfWithProgress(
    dto,
    (progress) => {
      setDlModal((prev) => ({
        ...prev,
        percent: progress.percent,
        message: progress.message,
      }));
      if (progress.message === 'Done' && progress.percent >= 100) {
        downloadPdf(dto)
          .then(() => {
            setTimeout(() => setDlModal({ open: false, percent: 0, message: '', error: null }), 1500);
          })
          .catch(() => {
            setDlModal((prev) => ({ ...prev, error: 'Download failed after generation.' }));
          });
      }
    },
    (error) => {
      setDlModal((prev) => ({ ...prev, error }));
    }
  );
};

  const closeDlModal = () => {
    setDlModal({ open: false, percent: 0, message: '', error: null });
  };

  return (
    <div className={`pre-layout ${isDark ? 'pre-dark' : 'pre-light'}`}>
      {/* TOP MENU */}
      <div className="pre-topmenu-wrap">
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
      </div>

      {/* Top action bar */}
      <div className={`pre-topbar ${isDark ? 'pre-topbar-dark' : ''}`}>
        <button className="pre-topbar-btn pre-topbar-upload" onClick={() => uploadRef.current?.click()} disabled={uploadLoading}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          {uploadLoading ? 'Parsing...' : 'Upload Resume'}
        </button>
        <input
          ref={uploadRef}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleUploadResume}
          hidden
        />
        <button className="pre-topbar-btn pre-topbar-download" onClick={handleDownload}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Download PDF
        </button>
      </div>

      <div className="pre-body">
        {/* LEFT SIDEBAR */}
        <aside className={`pre-sidebar ${sidebarCollapsed ? 'pre-sidebar-collapsed' : ''}`}>
          <div className="pre-sidebar-top">
            <button
              className="pre-sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {sidebarCollapsed
                  ? <polyline points="9 18 15 12 9 6"/>
                  : <polyline points="15 18 9 12 15 6"/>
                }
              </svg>
            </button>
          </div>

          <nav className="pre-sidebar-nav">
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                className={`pre-sidebar-item ${activeSection === s.key ? 'pre-sidebar-item-active' : ''}`}
                onClick={() => setActiveSection(s.key)}
                title={s.label}
              >
                <span className="pre-sidebar-icon">{s.icon}</span>
                {!sidebarCollapsed && <span className="pre-sidebar-label">{s.label}</span>}
              </button>
            ))}
          </nav>

          <div className="pre-sidebar-bottom">
            <button
              className="pre-download-btn"
              onClick={handleDownload}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              {!sidebarCollapsed && <span>Download PDF</span>}
            </button>
            {!sidebarCollapsed && (
              <>
                <div className="pre-sidebar-footer-links">
                  <a href="#terms">Terms</a>
                  <span>|</span>
                  <a href="#privacy">Privacy policy</a>
                  <span>|</span>
                  <a href="#contact">Contact us</a>
                </div>
                <div className="pre-sidebar-copyright">All rights reserved.</div>
              </>
            )}
          </div>
        </aside>

        {/* CENTER FORM */}
        <main className="pre-form-panel">
          <div className="pre-form-inner">
            {activeSection === 'header' && (
              <div className="pre-section">
                <h2 className="pre-section-title">How do you want recruiters to contact you?</h2>
                <p className="pre-section-hint">Include your full name and at least email or phone number</p>

                <div className="pre-photo-row">
                  <label className="pre-photo-label">
                    {data.profileImageBase64 ? (
                      <img
                        src={`data:image/jpeg;base64,${data.profileImageBase64}`}
                        alt="profile"
                        className="pre-photo-thumb"
                      />
                    ) : (
                      <div className="pre-photo-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} hidden />
                  </label>
                  <button className="pre-photo-upload-btn" onClick={() => document.querySelector('.pre-photo-label input').click()}>
                    Photo upload
                  </button>
                </div>

                <div className="pre-field-row">
                  <div className="pre-field">
                    <label className="pre-label">First name</label>
                    <input value={data.firstName} onChange={(e) => handleChange('firstName', e.target.value)} />
                  </div>
                  <div className="pre-field">
                    <label className="pre-label">Surname</label>
                    <input value={data.surname} onChange={(e) => handleChange('surname', e.target.value)} />
                  </div>
                </div>

                <div className="pre-field">
                  <label className="pre-label">City</label>
                  <input value={data.city} onChange={(e) => handleChange('city', e.target.value)} />
                </div>

                <div className="pre-field-row">
                  <div className="pre-field">
                    <label className="pre-label">Country</label>
                    <input value={data.country} onChange={(e) => handleChange('country', e.target.value)} />
                  </div>
                  <div className="pre-field">
                    <label className="pre-label">Pin code</label>
                    <input value={data.pinCode} onChange={(e) => handleChange('pinCode', e.target.value)} />
                  </div>
                </div>

                <div className="pre-field-row">
                  <div className="pre-field">
                    <label className="pre-label">Phone</label>
                    <input value={data.phone} onChange={(e) => handleChange('phone', e.target.value)} />
                  </div>
                  <div className="pre-field">
                    <label className="pre-label">Email</label>
                    <div className="pre-input-with-icon">
                      <input value={data.email} onChange={(e) => handleChange('email', e.target.value)} />
                      {data.email && data.email.includes('@') && (
                        <span className="pre-input-check">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="9 12 12 15 16 10"/></svg>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="pre-action-row">
                  <button className="pre-btn-back" onClick={() => navigate(-1)}>Back</button>
                  <button className="pre-btn-continue" onClick={() => setActiveSection('experience')}>Continue</button>
                </div>
              </div>
            )}

            {activeSection === 'experience' && (
              <div className="pre-section">
                <h2 className="pre-section-title">Tell us about your work experience</h2>
                <p className="pre-section-hint">Start with your most recent position</p>
                {data.experiences.map((exp, i) => (
                  <div className="pre-sub-card" key={i}>
                    <div className="pre-sub-card-header">
                      <div className="pre-sub-label">Experience {i + 1}</div>
                      {data.experiences.length > 1 && (
                        <button className="pre-remove-btn" onClick={() => removeExperience(i)} title="Remove">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="pre-field-row">
                      <div className="pre-field">
                        <label className="pre-label">Job Title</label>
                        <input value={exp.jobTitle} onChange={(e) => handleExpChange(i, 'jobTitle', e.target.value)} />
                      </div>
                      <div className="pre-field">
                        <label className="pre-label">Company</label>
                        <input value={exp.company} onChange={(e) => handleExpChange(i, 'company', e.target.value)} />
                      </div>
                    </div>
                    <div className="pre-field-row">
                      <div className="pre-field">
                        <label className="pre-label">Start Date</label>
                        <input placeholder="e.g. Jan 2022" value={exp.startDate} onChange={(e) => handleExpChange(i, 'startDate', e.target.value)} />
                      </div>
                      <div className="pre-field">
                        <label className="pre-label">End Date</label>
                        <input placeholder="e.g. Present" value={exp.endDate} onChange={(e) => handleExpChange(i, 'endDate', e.target.value)} />
                      </div>
                    </div>
                    <div className="pre-field">
                      <label className="pre-label">Description (bullet points)</label>
                      {(Array.isArray(exp.description) ? exp.description : [exp.description || '']).map((pt, pi) => (
                        <div className="pre-bullet-row" key={pi}>
                          <span className="pre-bullet-dot">•</span>
                          <input
                            className="pre-bullet-input"
                            value={pt}
                            placeholder={`Point ${pi + 1}`}
                            onChange={(e) => handleDescPointChange(i, pi, e.target.value)}
                          />
                          {(Array.isArray(exp.description) ? exp.description : []).length > 1 && (
                            <button className="pre-remove-inline" onClick={() => removeDescPoint(i, pi)}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                          )}
                        </div>
                      ))}
                      <button className="pre-add-btn pre-add-point-btn" onClick={() => addDescPoint(i)}>+ Add Point</button>
                    </div>
                  </div>
                ))}
                <button className="pre-add-btn" onClick={() =>
                  setData(prev => ({
                    ...prev,
                    experiences: [...prev.experiences, { jobTitle: '', company: '', startDate: '', endDate: '', description: [''] }]
                  }))}>
                  + Add Experience
                </button>
                <div className="pre-action-row">
                  <button className="pre-btn-back" onClick={() => setActiveSection('header')}>Back</button>
                  <button className="pre-btn-continue" onClick={() => setActiveSection('education')}>Continue</button>
                </div>
              </div>
            )}

            {activeSection === 'education' && (
              <div className="pre-section">
                <h2 className="pre-section-title">Education background</h2>
                <p className="pre-section-hint">Add your education details</p>
                {data.educations.map((edu, i) => (
                  <div className="pre-sub-card" key={i}>
                    <div className="pre-sub-card-header">
                      <div className="pre-sub-label">Education {i + 1}</div>
                      {data.educations.length > 1 && (
                        <button className="pre-remove-btn" onClick={() => removeEducation(i)} title="Remove">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      )}
                    </div>
                    <div className="pre-field-row">
                      <div className="pre-field">
                        <label className="pre-label">Degree / Course</label>
                        <input value={edu.degree} onChange={(e) => handleEduChange(i, 'degree', e.target.value)} />
                      </div>
                      <div className="pre-field">
                        <label className="pre-label">Institution</label>
                        <input value={edu.institution} onChange={(e) => handleEduChange(i, 'institution', e.target.value)} />
                      </div>
                    </div>
                    <div className="pre-field">
                      <label className="pre-label">Year</label>
                      <input value={edu.year} onChange={(e) => handleEduChange(i, 'year', e.target.value)} />
                    </div>
                  </div>
                ))}
                <button className="pre-add-btn" onClick={() =>
                  setData(prev => ({
                    ...prev,
                    educations: [...prev.educations, { degree: '', institution: '', year: '' }]
                  }))}>
                  + Add Education
                </button>
                <div className="pre-action-row">
                  <button className="pre-btn-back" onClick={() => setActiveSection('experience')}>Back</button>
                  <button className="pre-btn-continue" onClick={() => setActiveSection('skills')}>Continue</button>
                </div>
              </div>
            )}

            {activeSection === 'skills' && (
              <div className="pre-section">
                <h2 className="pre-section-title">What skills would you like to highlight?</h2>
                <p className="pre-section-hint">Add skills relevant to the job you are applying for</p>
                <div className="pre-skills-grid">
                  {data.skills.map((skill, i) => (
                    <div className="pre-field pre-skill-field" key={i}>
                      <label className="pre-label">Skill {i + 1}</label>
                      <div className="pre-input-with-remove">
                        <input value={skill} onChange={(e) => handleSkillChange(i, e.target.value)} />
                        {data.skills.length > 1 && (
                          <button className="pre-remove-inline" onClick={() => removeSkill(i)}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <button className="pre-add-btn" onClick={() =>
                  setData(prev => ({ ...prev, skills: [...prev.skills, ''] }))}>
                  + Add Skill
                </button>
                <div className="pre-action-row">
                  <button className="pre-btn-back" onClick={() => setActiveSection('education')}>Back</button>
                  <button className="pre-btn-continue" onClick={() => setActiveSection('summary')}>Continue</button>
                </div>
              </div>
            )}

            {activeSection === 'summary' && (
              <div className="pre-section">
                <h2 className="pre-section-title">Professional Summary</h2>
                <p className="pre-section-hint">Write 2-4 sentences about your experience and strengths</p>
                <div className="pre-field">
                  <label className="pre-label">Summary</label>
                  <textarea
                    value={data.summary}
                    rows={6}
                    onChange={(e) => handleChange('summary', e.target.value)}
                  />
                </div>
                <div className="pre-action-row">
                  <button className="pre-btn-back" onClick={() => setActiveSection('skills')}>Back</button>
                  <button className="pre-btn-continue" onClick={handleDownload}>
                    Finish & Download
                  </button>
                </div>
              </div>
            )}

            {activeSection === 'other' && (
              <div className="pre-section">
                <h2 className="pre-section-title">Other Sections</h2>
                <p className="pre-section-hint">Add additional sections like certifications, languages, projects, etc.</p>
                <p className="pre-coming-soon">Coming soon...</p>
                <div className="pre-action-row">
                  <button className="pre-btn-back" onClick={() => setActiveSection('summary')}>Back</button>
                </div>
              </div>
            )}
          </div>
        </main>

        {/* RIGHT LIVE PREVIEW */}
        <div className="pre-preview-panel">
          <PuppeteerLivePreviewer html={previewHtml} loading={previewLoading} theme={theme} />
          <div className="pre-preview-footer">
            <button className="pre-change-template" onClick={() => navigate('/resume-builder-v2')}>
              Change template
            </button>
            <p className="pre-preview-note">
              You can edit the content, use other fonts, adjust format, add sections, and change placement of the sections later on.
            </p>
          </div>
        </div>
      </div>

      {/* Resume Parsing Overlay */}
      <ResumeParsingOverlay active={uploadLoading} done={parseDone} />

      {/* Download Progress Modal */}
      <DownloadProgressModal
        open={dlModal.open}
        percent={dlModal.percent}
        message={dlModal.message}
        error={dlModal.error}
        onClose={closeDlModal}
      />
    </div>
  );
};

export default PuppeteerResumeEditor;
